import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Surface, Badge, Button, Modal } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Check, X, Clock, Gavel } from "@phosphor-icons/react";

const ENTITY_LABEL = { vehicle: "Vehicle", battery: "Battery", tyre: "Tyre", mileage_log: "Mileage log" };
const labelFor = (t) => ENTITY_LABEL[t] || (t ? t.replace(/_/g, " ") : "Item");

export default function Approvals() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [list, setList] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [decisionFor, setDecisionFor] = useState(null); // { row, action: "approve"|"reject" }
    const [decisionReason, setDecisionReason] = useState("");

    const load = () => {
        if (!isAdmin) return Promise.resolve();
        const url = statusFilter ? `/approvals?status=${statusFilter}` : "/approvals";
        return api.get(url).then((r) => setList(r.data));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, isAdmin]);

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
        for (const r of list) c[r.status] = (c[r.status] || 0) + 1;
        return c;
    }, [list]);

    if (user && !isAdmin) return <Navigate to="/" replace />;

    const submitDecision = async () => {
        if (!decisionFor) return;
        try {
            await api.post(
                `/approvals/${decisionFor.row.id}/${decisionFor.action}`,
                { reason: decisionReason.trim() || null },
            );
            toast.success(decisionFor.action === "approve" ? "Request approved · entity deleted" : "Request rejected");
            setDecisionFor(null);
            setDecisionReason("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Decision failed");
        }
    };

    const cancelOwn = async (row) => {
        if (!window.confirm("Cancel this deletion request?")) return;
        try {
            await api.delete(`/approvals/${row.id}`);
            toast.success("Request cancelled");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel");
        }
    };

    const STATUS_TABS = [
        { key: "pending", label: "Pending", icon: Clock },
        { key: "approved", label: "Approved", icon: Check },
        { key: "rejected", label: "Rejected", icon: X },
        { key: "", label: "All", icon: Gavel },
    ];

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="GOVERNANCE"
                title="Deletion approvals"
                subtitle={isAdmin
                    ? "Authorize or reject pending deletion requests from your team"
                    : "Track deletion requests you have submitted"}
            />

            <Surface className="px-4 py-3 mb-4 flex items-center gap-2 flex-wrap">
                {STATUS_TABS.map((t) => {
                    const Icon = t.icon;
                    const active = statusFilter === t.key;
                    return (
                        <button
                            key={t.key || "all"}
                            onClick={() => setStatusFilter(t.key)}
                            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors border ${
                                active
                                    ? "bg-fleet-ink text-white border-fleet-ink"
                                    : "bg-white text-fleet-inkSoft border-fleet-border hover:bg-slate-50"
                            }`}
                            data-testid={`approvals-tab-${t.key || "all"}`}
                        >
                            <Icon size={14} />
                            {t.label}
                            {t.key && counts[t.key] > 0 && (
                                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${active ? "bg-white/20" : "bg-slate-100"}`}>
                                    {counts[t.key]}
                                </span>
                            )}
                        </button>
                    );
                })}
                <span className="ml-auto text-xs text-fleet-inkSoft">{list.length} record(s)</span>
            </Surface>

            <Surface className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left">
                            {["TYPE", "ENTITY", "REASON", "REQUESTED BY", "REQUESTED", "STATUS", "DECIDED BY", ""].map((h) => (
                                <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((row) => (
                            <tr key={row.id} className="border-t border-fleet-border" data-testid={`approval-row-${row.id}`}>
                                <td className="px-5 py-3 capitalize">{labelFor(row.entity_type)}</td>
                                <td className="px-5 py-3 font-mono text-xs">{row.entity_summary}</td>
                                <td className="px-5 py-3 text-fleet-inkSoft text-xs max-w-xs truncate" title={row.reason || ""}>
                                    {row.reason || "—"}
                                </td>
                                <td className="px-5 py-3">
                                    <div>{row.requested_by_name}</div>
                                    <div className="text-xs text-fleet-inkSoft">{row.requested_by_email}</div>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{formatDate(row.requested_at)}</td>
                                <td className="px-5 py-3">
                                    <Badge variant={
                                        row.status === "approved" ? "active"
                                            : row.status === "rejected" ? "decommissioned"
                                            : row.status === "cancelled" ? "idle"
                                            : "in_service"
                                    }>{row.status}</Badge>
                                </td>
                                <td className="px-5 py-3 text-xs">
                                    {row.decided_by_name ? (
                                        <>
                                            <div>{row.decided_by_name}</div>
                                            <div className="text-fleet-inkSoft">{formatDate(row.decided_at)}</div>
                                            {row.decision_reason && (
                                                <div className="text-fleet-inkSoft italic mt-0.5">"{row.decision_reason}"</div>
                                            )}
                                        </>
                                    ) : "—"}
                                </td>
                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                    {row.status === "pending" && isAdmin && (
                                        <>
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => { setDecisionFor({ row, action: "approve" }); setDecisionReason(""); }}
                                                data-testid={`approve-${row.id}`}
                                            >
                                                <Check size={14} /> Approve
                                            </Button>
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => { setDecisionFor({ row, action: "reject" }); setDecisionReason(""); }}
                                                data-testid={`reject-${row.id}`}
                                            >
                                                <X size={14} /> Reject
                                            </Button>
                                        </>
                                    )}
                                    {row.status === "pending" && !isAdmin && row.requested_by === user?.id && (
                                        <Button variant="ghost" size="sm" onClick={() => cancelOwn(row)}>Cancel</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-5 py-12 text-center text-sm text-fleet-inkSoft">
                                    No {statusFilter || ""} requests.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Surface>

            <Modal
                open={!!decisionFor}
                onClose={() => { setDecisionFor(null); setDecisionReason(""); }}
                title={decisionFor?.action === "approve" ? "Approve deletion?" : "Reject deletion?"}
                footer={<>
                    <Button variant="secondary" onClick={() => { setDecisionFor(null); setDecisionReason(""); }}>Cancel</Button>
                    <Button
                        onClick={submitDecision}
                        data-testid="confirm-decision"
                    >
                        {decisionFor?.action === "approve" ? "Approve & delete" : "Reject"}
                    </Button>
                </>}
            >
                {decisionFor && (
                    <div className="space-y-4">
                        <div className="bg-slate-50 border border-fleet-border rounded-md p-3 text-sm">
                            <div className="text-xs text-fleet-inkSoft uppercase tracking-wider mb-1">{labelFor(decisionFor.row.entity_type)}</div>
                            <div className="font-mono">{decisionFor.row.entity_summary}</div>
                            <div className="text-xs text-fleet-inkSoft mt-2">
                                Requested by {decisionFor.row.requested_by_name} · {formatDate(decisionFor.row.requested_at)}
                            </div>
                            {decisionFor.row.reason && (
                                <div className="text-xs text-fleet-inkSoft italic mt-1">"{decisionFor.row.reason}"</div>
                            )}
                        </div>
                        <div>
                            <label className="label-mono block mb-1.5">
                                {decisionFor.action === "approve" ? "Approval note (optional)" : "Rejection reason"}
                            </label>
                            <textarea
                                value={decisionReason}
                                onChange={(e) => setDecisionReason(e.target.value)}
                                rows={3}
                                placeholder={decisionFor.action === "approve"
                                    ? "Optional note for the audit log"
                                    : "Tell the requester why this was rejected"}
                                className="w-full px-3 py-2 border border-fleet-border rounded-md text-sm focus:outline-none focus:border-fleet-ink"
                                data-testid="decision-reason-input"
                            />
                        </div>
                        {decisionFor.action === "approve" && (
                            <div className="text-xs text-fleet-inkSoft border-l-2 border-fleet-ink pl-3">
                                Approving will soft-delete the {labelFor(decisionFor.row.entity_type).toLowerCase()}.
                                History (fitments, assignments, work orders) is preserved but the record will no longer appear in lists.
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
